import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export interface MyJob {
  id: number;
  project: number;
  tech: number;
  tech_name: string;
  project_name: string;
  project_number: string;
  project_location: string;
  role: string;
  start_date: string;
  end_date: string | null;
  daily_rate: string | null;
  notes: string;
  supervisor: string | null;
  created_at: string;
}

export interface MyJSA {
  id: number;
  title: string;
  project: number | null;
  project_display: { id: number; name: string; project_number: string } | null;
  project_name: string;
  project_number: string;
  location: string;
  date: string;
  status: string;
  created_by: number;
  created_by_username: string;
  has_signed: boolean;
  created_at: string;
}

export interface MyGear {
  id: number;
  asset: number;
  asset_name: string;
  asset_serial_number: string;
  asset_type: string;
  asset_manufacturer: string;
  asset_model: string;
  action: string;
  due_date: string | null;
  timestamp: string;
  notes: string;
}

export interface MyCert {
  id: number;
  cert_type: string;
  cert_name: string;
  cert_number: string;
  issuing_body: string;
  issue_date: string;
  expiry_date: string | null;
  status: string;
  notes: string;
  days_until_expiry: number | null;
  expiry_warning: 'red' | 'amber' | null;
  created_at: string;
  updated_at: string;
}

export interface RecentActivity {
  id: number;
  action: string;
  object_repr: string;
  timestamp: string;
}

export interface MyDashboardData {
  my_jobs: MyJob[];
  my_jobs_count: number;
  pending_jsas: MyJSA[];
  pending_jsas_count: number;
  my_gear: MyGear[];
  my_gear_count: number;
  my_certs: MyCert[];
  my_certs_count: number;
  expiring_certs_count: number;
  recent_activity: RecentActivity[];
}

export function useMyDashboard() {
  return useQuery<MyDashboardData>({
    queryKey: ['my-dashboard'],
    queryFn: async () => {
      const response = await apiClient.get<MyDashboardData>('/api/v1/me/dashboard/');
      return response.data;
    },
  });
}

// ─── Compliance standing (QP-300 clause 2.4, read-only) ─────────────────────

export interface MyComplianceStanding {
  clause_ref: string;
  clause_title: string;
  summary: {
    total: number;
    valid: number;
    expiring_soon: number;
    expired: number;
    revoked: number;
  };
  certs: MyCert[];
}

export function useMyComplianceStanding(enabled = true) {
  return useQuery<MyComplianceStanding>({
    queryKey: ['my-compliance-standing'],
    queryFn: async () => {
      const response = await apiClient.get<MyComplianceStanding>(
        '/api/v1/me/compliance-standing/',
      );
      return response.data;
    },
    enabled,
  });
}
